/**
 * Central helper for making GraphQL calls that report their lifecycle through Redux.
 *
 * Given [request, success, failure] action types (strings or RTK action creators), it
 * dispatches the request action, runs the query via fetchGraphQLApi (src/helpers/callApi),
 * then dispatches success or failure with the variables/query/response in meta, tagged with
 * an _internalRequestId so handleApiCall reducers can track the request. Reads endpoint and
 * auth token from glc-redux config/auth state; dispatches logout() on authentication_error.
 * Gotcha: it throws on GraphQL errors even when throwOnError is false, except when errors
 * are all not_found (returns undefined, or partial data if returnPartialDataOnNotFound).
 */
import { constant, isFunction, isString } from 'lodash';
import { modules, ConfigState } from '@veritone/glc-redux';

import { fetchGraphQLApi } from './callApi';
import {
  extendedError,
  guid,
  isObjectWithStringProperty,
} from '@common/shared/util';

import { debug } from 'debug';
import {
  // ActionCreatorWithPayload,
  // ActionCreatorWithoutPayload,
  ActionCreatorWithPreparedPayload,
  createAction,
} from '@reduxjs/toolkit';
import { AsyncOrSync } from 'ts-essentials';
// eslint-disable-next-line testing-library/no-debugging-utils
const graphqlDebug = debug('graphql');
const LONG_QUERY_TIME_MS = 5000;

const {
  config: { getConfig: getBaseConfig },
  auth: { selectOAuthToken, selectSessionToken },
  user: { logout },
} = modules;

// @ts-expect-error TODO: ConfigState type from glc-redux probably needs to allow for readonly values
interface ExtendedConfigState extends ConfigState {
  veritoneAppId: string;
}

function getConfig(state: any) {
  return getBaseConfig(state) as ExtendedConfigState;
}

export type GQLApiDispatch<T = unknown> = ({
  type,
  payload,
  meta,
  error,
}: {
  type: string;
  payload?: Awaited<ReturnType<typeof fetchGraphQLApi<T>>>['data'] | any[];
  meta?: {
    response?: Awaited<ReturnType<typeof fetchGraphQLApi<T>>>;
    variables: Record<string, any>;
    operationName?: string;
    query: string;
    _internalRequestId: string;
    _shouldTrackRequestsIndividually: boolean;
  };
  error?: boolean;
}) => AsyncOrSync<void>;

// export type GQLApiGetState<T = unknown> = NonNullable<
//   Parameters<typeof callGraphQLApi<T>>[0]['getState']
// >;
export type GQLApiGetState = () => any;

// TODO: Bring back wehn we remove support for any strings below
// type NoopStrings =
//   | 'noop'
//   | 'notifications/noop'
//   | 'CBSA/noop'
//   | 'CBSA/NOOP'
//   | 'CBSA/MainPage/NOOP'
//   | '';

type GQLFailureType<T = any> =
  | ActionCreatorWithPreparedPayload<any[], T, string, boolean | undefined, any>
  // | NoopStrings
  | string;
type GQLSuccessType<T> =
  | ActionCreatorWithPreparedPayload<any[], T, string, boolean | undefined, any>
  // | NoopStrings
  | string;
type GQLRequestType =
  | ActionCreatorWithPreparedPayload<any[], undefined, string, never, any>
  // | NoopStrings
  | string;
// type GQLActionType<T = any> =
//   | string
//   | ActionCreatorWithPayload<T, string>
//   | ActionCreatorWithoutPayload<string>;

interface RequestMeta<V> {
  variables: V;
  operationName: string | undefined;
  query: string;
  _internalRequestId: string;
  _shouldTrackRequestsIndividually: boolean;
}
// TODO: Should probably pass payload first, then meta
export function createGraphQLRequestAction<V = Record<string, any>, VR = false>(
  type: string
) {
  return createAction(
    type,
    (meta: VR extends true ? RequestMeta<V> : RequestMeta<V> | undefined) => ({
      payload: undefined,
      meta,
    })
  );
}

interface SuccessMeta<V> {
  variables: V;
  operationName: string | undefined;
  query: string;
  _internalRequestId: string;
  _shouldTrackRequestsIndividually: boolean;
  response?: {
    status: number;
    // data?: T | undefined;
    data?: any; // TODO: IS this just the payload type?
    errors?: any[];
  };
}

export function createGraphQLSuccessAction<
  P,
  V = Record<string, any>,
  VR = false,
>(type: string) {
  return createAction(
    type,
    (
      payload: P,
      meta: VR extends true ? SuccessMeta<V> : SuccessMeta<V> | undefined,
      error?: false
    ) => ({
      payload,
      meta,
      error,
    })
  );
}

interface FailureMeta<T = any, V = Record<string, any>> {
  response?:
    | {
        status: number;
        data?: T | undefined;
        errors?: any[];
      }
    | undefined;
  variables: V;
  operationName?: string;
  query?: string;
  _internalRequestId?: string;
  _shouldTrackRequestsIndividually?: boolean;
}

export function createGraphQLFailureAction<
  E = unknown,
  V = Record<string, any>,
  VR = false,
>(type: string) {
  return createAction(
    type,
    (
      payload: E,
      meta: VR extends true
        ? FailureMeta<any, V>
        : FailureMeta<any, V> | undefined,
      error?: boolean
    ) => ({
      payload,
      meta,
      error,
    })
  );
}

interface TCallGraphQLApi<T> {
  // actionTypes: Readonly<[GQLActionType, GQLSuccessType<T>, GQLFailureType]>;
  actionTypes: Readonly<[GQLRequestType, GQLSuccessType<T>, GQLFailureType]>;
  query: string;
  variables: Record<string, any>;
  operationName?: string;
  bailout?: (v: any) => boolean;
  dispatch?: GQLApiDispatch<T>;
  getState?: GQLApiGetState;
  // optional requestId which can be given to the selectors generated by
  // handleApiCall to track state of an individual request (by default only the
  // latest request is tracked)
  requestId?: string;
  throwOnError?: boolean;
  keepAlive?: boolean;
  /** When true, return partial `data` if errors are only `not_found` (batch queries). */
  returnPartialDataOnNotFound?: boolean;
}

async function callGraphQLApi<T = unknown>({
  actionTypes, // : [requestType, successType, failureType],
  query,
  variables,
  operationName,
  bailout,
  dispatch,
  getState,
  throwOnError,
  requestId,
  keepAlive,
  returnPartialDataOnNotFound,
}: TCallGraphQLApi<T> & { throwOnError: true }): Promise<T>;
async function callGraphQLApi<T>({
  actionTypes, // : [requestType, successType, failureType],
  query,
  variables,
  operationName,
  bailout,
  dispatch,
  getState,
  throwOnError,
  requestId,
  keepAlive,
  returnPartialDataOnNotFound,
}: TCallGraphQLApi<T>): Promise<T | undefined>;
async function callGraphQLApi<T = unknown>({
  actionTypes, // : [requestType, successType, failureType],
  query,
  variables,
  operationName = undefined,
  bailout = constant(false),
  dispatch,
  getState,
  throwOnError,
  // optional requestId which can be given to the selectors generated by
  // handleApiCall to track state of an individual request (by default only the
  // latest request is tracked)
  requestId = undefined,
  keepAlive,
  returnPartialDataOnNotFound = false,
}: {
  // actionTypes: Readonly<[GQLActionType, GQLSuccessType<T>, GQLFailureType]>;
  actionTypes: Readonly<[GQLRequestType, GQLSuccessType<T>, GQLFailureType]>;
  query: string;
  variables: Record<string, any>;
  operationName?: string;
  bailout?: (v: any) => boolean;
  dispatch?: GQLApiDispatch<T>;
  getState?: GQLApiGetState;
  requestId?: string;
  throwOnError?: boolean;
  keepAlive?: boolean;
  returnPartialDataOnNotFound?: boolean;
}) {
  if (!isFunction(dispatch) || !isFunction(getState)) {
    throw new Error('callGraphQLApi requires dispatch and getState functions');
  }
  // const [requestType, successType, failureType] = actionTypes.map(
  //   (actionType) => (isString(actionType) ? actionType : getType(actionType))
  // ) as [string, string, string]; // TODO: Fix map shouldn't remove tuple type - why does it?
  const [requestType, successType, failureType] = actionTypes;

  // TODO: Is there any typing we can do around the required state type here?
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const state = getState();
  const config = getConfig(state);
  const endpoint = `${config.apiRoot}/${config.graphQLEndpoint}`;
  const token = selectOAuthToken(state) || selectSessionToken(state);

  const shouldBail = bailout(state);
  if (shouldBail) {
    return;
  }

  // attach an ID so the handleApiCall reducer can track this request across
  // its multiple actions
  const _internalRequestId = requestId || guid();
  const meta = {
    variables,
    operationName,
    query,
    _internalRequestId,
    _shouldTrackRequestsIndividually: !!requestId,
  };
  if (isString(requestType)) {
    dispatch({
      type: requestType,
      meta,
    });
  } else {
    dispatch(requestType(meta));
  }
  let response;
  const startTime = Date.now();
  let endTime: number | null = null;
  const timerId = window.setTimeout(() => {
    endTime || graphqlDebug(`GraphQL query taking over 5 seconds: ${query}`);
  }, LONG_QUERY_TIME_MS);

  try {
    response = await fetchGraphQLApi<T>({
      endpoint,
      query,
      variables,
      operationName,
      token,
      veritoneAppId: config.veritoneAppId,
      keepAlive,
    });
  } catch (e) {
    if (isString(failureType)) {
      dispatch({
        type: failureType,
        error: true,
        // TODO: Remove support for string failureType
        // or update dispatch type to better type the payload in error case
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        payload: e as any,
        meta: { ...meta, response },
      });
    } else {
      dispatch(failureType(e, { ...meta, response }, true));
    }
    // wrap this single error for consistency with graphQL errors, which are always
    // wrapped.
    throw extendedError(
      isObjectWithStringProperty(e, 'message') ? e.message : 'Unknown Error',
      [e]
    );
  }

  if (response.errors?.length) {
    if (throwOnError) {
      throw new Error(
        `Status ${response.status}: ${response.errors[0].message}`
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const errorNotFound = response.errors.find(
      (item: any) => item.name === 'not_found'
    );
    const onlyNotFoundErrors = response.errors.every(
      (item: any) => item.name === 'not_found'
    );

    if (
      returnPartialDataOnNotFound &&
      errorNotFound &&
      onlyNotFoundErrors &&
      response.data
    ) {
      endTime = Date.now();
      if (endTime - startTime > LONG_QUERY_TIME_MS) {
        graphqlDebug(`Request took ${endTime - startTime}ms`);
      }
      if (isString(successType)) {
        dispatch({
          type: successType,
          payload: response.data,
          meta: { ...meta, response },
        });
      } else {
        dispatch(successType(response.data, { ...meta, response }));
      }
      if (timerId) {
        clearTimeout(timerId);
      }
      return response.data;
    }

    if (isString(failureType)) {
      dispatch({
        type: failureType,
        error: true,
        payload: response.errors,
        meta: { ...meta, response },
      });
    } else {
      dispatch(failureType(response.errors, { ...meta, response }, true));
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const errorNotFoundAfterFailure = response.errors.find(
      (item: any) => item.name === 'not_found'
    );
    if (errorNotFoundAfterFailure) {
      console.log(response.errors);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const errorAuth = response.errors.find(
      (item: any) => item.name === 'authentication_error'
    );
    if (errorAuth) {
      dispatch(logout());
    }

    // TODO: Why are we throwing if throwOnError is false?
    throw extendedError(
      `Status ${response.status}: ${response.errors[0].message}`,
      response.errors
    );
  }

  endTime = Date.now();
  if (endTime - startTime > LONG_QUERY_TIME_MS) {
    graphqlDebug(`Request took ${endTime - startTime}ms`);
  }

  if (isString(successType)) {
    dispatch({
      type: successType,
      payload: response.data,
      meta: { ...meta, response },
    });
  } else {
    dispatch(successType(response.data, { ...meta, response }));
  }

  if (timerId) {
    clearTimeout(timerId);
  }
  return response.data;
}

export default callGraphQLApi;
